import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  Paper,
  TableContainer,
  Table as MuiTable,
  TableBody,
  LinearProgress,
  Box,
} from '@mui/material'
import { motion, LayoutGroup } from 'framer-motion'

import NoDataOrLoadingIndicator from '../../../../components/shared/table/table-mobile-no-data'

import { styles } from './../table.styles'
import TableHead from './table-head.component'
import TableRow from './table-row.component'

// Animation variants for table rows stagger effect
const tableRowsStaggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.05,
      staggerChildren: 0.04,
    },
  },
}

const tableRowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
}

const TableDesktopContent = forwardRef(
  (
    {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      moveRow,
      onDrop,
      hasDragAndDrop,
      sorting,
      isLoading,
      infiniteScrollProps,
      customStyle,
      customEmptyState,
      loadingSize,
      // Extract props that shouldn't be spread onto DOM elements
      headerActions: _headerActions,
      ...props
    },
    ref
  ) => {
    const hasData = Boolean(rows && rows.length > 0)
    const showLoadingBar = Boolean(isLoading || infiniteScrollProps?.isRefetching) && hasData
    const showEmptyState = !hasData
    const showLoadingIndicator = isLoading

    return (
      <DndProvider backend={HTML5Backend}>
        <Box sx={{ position: 'relative' }}>
          {showLoadingBar && (
            <LinearProgress
              sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000, height: 3 }}
            />
          )}
          <Paper sx={[customStyle ?? styles.paperContainer]}>
            <TableContainer sx={styles.tableContainer} ref={ref}>
              <MuiTable {...getTableProps()} sx={[styles.table, styles.tableHeader]} {...props}>
                <TableHead
                  headerGroups={headerGroups}
                  hasDragAndDrop={hasDragAndDrop}
                  sx={styles.stickyHeader}
                  sorting={sorting}
                />
                {hasData && !showLoadingIndicator && (
                  <TableBody
                    {...getTableBodyProps()}
                    component={motion.tbody}
                    variants={tableRowsStaggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    <LayoutGroup>
                      {rows.map((row, index) => {
                        prepareRow(row)
                        const rowKey =
                          hasDragAndDrop && row.original?.id
                            ? row.original.id
                            : row.getRowProps().key
                        return (
                          <TableRow
                            key={rowKey}
                            row={row}
                            index={index}
                            moveRow={moveRow}
                            onDrop={onDrop}
                            hasDragAndDrop={hasDragAndDrop}
                            motionVariants={tableRowVariants}
                          />
                        )
                      })}
                    </LayoutGroup>
                  </TableBody>
                )}
              </MuiTable>

              {(showEmptyState || showLoadingIndicator) &&
                (customEmptyState || (
                  <NoDataOrLoadingIndicator isLoading={isLoading} loadingSize={loadingSize} />
                ))}
            </TableContainer>
          </Paper>
        </Box>
      </DndProvider>
    )
  }
)

TableDesktopContent.propTypes = {
  getTableProps: PropTypes.func.isRequired,
  getTableBodyProps: PropTypes.func.isRequired,
  headerGroups: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  prepareRow: PropTypes.func.isRequired,
  moveRow: PropTypes.func.isRequired,
  onDrop: PropTypes.func,
  hasDragAndDrop: PropTypes.bool,
  sorting: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isLoading: PropTypes.bool.isRequired,
  infiniteScrollProps: PropTypes.object,
  customStyle: PropTypes.object,
  customEmptyState: PropTypes.node,
  loadingSize: PropTypes.oneOf(['small', 'medium', 'large']),
}

export default TableDesktopContent
